import clsx from '@vangst/lib/clsx'
import { fromISO } from '@vangst/lib/formatDates'
import unionize from '@vangst/lib/unionize'
import { Member_JobApplicationFragment } from '@vangst/services/oogst/types'
import Routes from '@vangst/services/routes'
import { memo, useMemo } from 'react'
import { MdBadge, MdCheckCircle, MdCheckCircleOutline } from 'react-icons/md'
import AlertText from '../../components/feedback/AlertText'
import ClickyLink from '../../components/navigation/ClickyLink'
import Avatar from '../../components/views/Avatar'

type PropsType = React.HTMLAttributes<HTMLElement> & {
  as: 'avatar' | 'card' | 'slip'
  jobApplication: Member_JobApplicationFragment
}

function MemberJobApplicationRenderer(props: PropsType) {
  const { as, jobApplication } = props
  const { jobPosting } = jobApplication

  // TODO: This should get reworked tearing applications apart in the 8/9-9/3 cycle
  const derived = useMemo(
    () => ({
      citystate: unionize(
        jobPosting?.location?.city,
        jobPosting?.location?.state,
        ', ',
      ),
      clientInitials: jobPosting?.client?.name[0] ?? '...',
      clientInterestAt:
        jobApplication.clientInterestAt != null
          ? fromISO(jobApplication.clientInterestAt)?.MMddyy
          : undefined,
      companyUrl: jobPosting?.client
        ? Routes.COMPANIES_DETAIL.replace(
            ':displayname',
            jobPosting.client.displayname,
          )
        : '#',
      jobUrl: jobPosting
        ? Routes.JOBS_DETAIL.replace(':id', jobPosting.slug)
        : '#',
      postedAt:
        jobPosting?.postedAt != null
          ? fromISO(jobPosting.postedAt)?.MMddyy
          : undefined,
      showReceived: jobPosting?.applicationReceived,
    }),
    [jobApplication.clientInterestAt, jobPosting],
  )

  if (as === 'avatar') {
    return (
      <ClickyLink
        className={clsx(
          'flex size-12 ease-bounce hocus:scale-105',
          jobPosting == null && 'scale-90',
          props.className,
        )}
        href={derived.jobUrl}
        title={jobPosting?.title || undefined}
      >
        <Avatar
          alt={derived.clientInitials}
          className="mood-grey"
          src={jobPosting?.client?.avatarUrl || undefined}
        />
      </ClickyLink>
    )
  }

  if (as === 'slip') {
    return (
      <div
        className={clsx(
          'boxlink mood-white relative flex h-36 flex-col border px-4 pb-2 pt-4',
          props.className,
        )}
      >
        <div className="h-20">
          <h2 className="line-clamp-2">{jobPosting?.title}</h2>
          <div className={clsx(derived?.showReceived && 'flex')}>
            <p className="mt-1 text-sm">
              <ClickyLink
                className="link-blue-dark relative z-10 mr-2 line-clamp-1 underline"
                href={derived.companyUrl}
                title={`@${jobPosting?.client?.displayname}`}
              >
                {jobPosting?.client?.name}
              </ClickyLink>
              <span className="line-clamp-1">{derived.citystate}</span>
            </p>
            {derived?.showReceived && (
              <AlertText
                className="ml-auto text-sm italic text-red"
                icon={MdCheckCircleOutline}
              >
                Received
              </AlertText>
            )}
          </div>
        </div>

        <div className="mt-auto flex h-8 justify-between">
          <ClickyLink
            as="text-icon"
            className="hitarea link-blue-dark"
            href={derived.jobUrl}
          >
            View Detail
          </ClickyLink>
        </div>
      </div>
    )
  }

  // as card
  return (
    <div
      className={clsx(
        'boxshadow boxlink mood-white relative flex h-80 w-full flex-col gap-4 rounded-md pt-4',
        props.className,
      )}
    >
      {/* Header */}
      <div className="grid grid-cols-[3rem,1fr] items-center gap-4 px-4">
        <Avatar
          alt={derived.clientInitials}
          className="mood-grey"
          src={jobPosting?.client?.avatarUrl || undefined}
        />
        <ClickyLink
          className="link-blue-dark z-10 line-clamp-1 text-sm underline"
          href={derived.companyUrl}
          title={`@${jobPosting?.client?.displayname}`}
        >
          {jobPosting?.client?.name}
        </ClickyLink>
      </div>

      {/* Body */}
      <div className="mt-4 px-4">
        <h2 className="line-clamp-1 text-lg">
          <ClickyLink href={derived.jobUrl} className="hitarea">
            {jobPosting.title}
          </ClickyLink>
        </h2>
        <div>
          <p className="line-clamp-1 text-sm">{derived?.citystate}</p>
          <p className="mb-2 text-sm text-grey">
            {derived.postedAt ? `Posted ${derived.postedAt}` : ''}
          </p>
          {jobApplication.clientInterestAt && (
            <p className="mb-2 w-full text-sm normal-case">
              <em>Interest Indicated {derived.clientInterestAt}</em>
            </p>
          )}
        </div>
        <ul className="flow-y-xs mt-4 px-4 text-sm capitalize">
          <li className="flex items-center gap-2 truncate">
            <MdBadge
              className={clsx(
                jobPosting?.requiredLicenses != null &&
                  jobPosting.requiredLicenses.length > 0
                  ? 'text-orange'
                  : 'text-grey-mid',
              )}
            />
            <span>
              {jobPosting?.requiredLicenses == null
                ? 'Badge •••'
                : jobPosting.requiredLicenses.length > 0
                ? 'Badge Required'
                : 'Badge Not Required'}
            </span>
          </li>
          {jobPosting?.applicationReceived && (
            <li className="flex items-center gap-2 truncate font-600 text-green">
              <MdCheckCircle className="text-green" />
              <span>Application Received</span>
            </li>
          )}
        </ul>
      </div>
      {/* <p className="mt-2 line-clamp-4 h-14 px-4 text-sm">
        <span>
          {jobPosting && jobPosting.description
            ? serializeJsonToReact(jobPosting.description)
            : 'Description Unavailable'}
        </span>
      </p> */}

      <div className="mb-4 mt-auto flex h-8 px-4">
        <ClickyLink
          as="text-icon"
          className="hitarea link-blue-dark"
          href={derived.jobUrl}
        >
          View Detail
        </ClickyLink>
      </div>
    </div>
  )
}

export default memo(MemberJobApplicationRenderer)
